import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ResponsiveContainer,
} from "recharts";
import { useState, useMemo } from "react";
import type { ApplicationDetailsRow, Candidate } from "../types/application";
import { useTranslation } from "react-i18next";

interface Props { applications: ApplicationDetailsRow[];}

export default function CandidateEvaluationChart({ applications }: Props) {
    const { t } = useTranslation();
    const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
    const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    const data: Candidate[] = useMemo(() => {
        return applications
            .filter(
                (app) =>
                    app.fitness_score != null &&
                    app.fakeness_score != null
            )
            .map((app) => ({
                name: app.applicant_name || t("candidateEvaluation.applicantFallback", { id: app.applicant_id }),
                alignment: Number(Number(app.fitness_score).toFixed(2)),
                authenticity: Number((Number(app.fakeness_score)).toFixed(2)),
            }));
    }, [applications, t]);

    const getCandidatesByQuadrant = (quadrant: string) => {
        const result = data.filter((c) => {
            if (quadrant === "highPerformers")
                return c.alignment >= 50 && c.authenticity >= 50;

            if (quadrant === "skilledRisky")
                return c.alignment >= 50 && c.authenticity < 50;

            if (quadrant === "genuineNeedsTraining")
                return c.alignment < 50 && c.authenticity >= 50;

            if (quadrant === "unsuitable")
                return c.alignment < 50 && c.authenticity < 50;

            return false;
        });

        const labels: Record<string, string> = {
            highPerformers: t("candidateEvaluation.quadrants.highPerformers"),
            skilledRisky: t("candidateEvaluation.quadrants.skilledRisky"),
            genuineNeedsTraining: t("candidateEvaluation.quadrants.genuineNeedsTraining"),
            unsuitable: t("candidateEvaluation.quadrants.unsuitable"),
        };

        setSelectedTitle(labels[quadrant] ?? quadrant);
        setFilteredCandidates(result);
        setIsPanelOpen(true);
    };

    const handleDotClick = (point: any) => {
        setSelectedTitle(point.name);
        setFilteredCandidates([point]);
        setIsPanelOpen(true);
    };

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const candidate = payload[0].payload;

            return (
                <div className="bg-white shadow-md border rounded-lg p-3 text-sm">
                    <p className="font-semibold">{candidate.name}</p>
                    <p>{t("candidateEvaluation.ras")}: {candidate.alignment}%</p>
                    <p>{t("candidateEvaluation.aq")}: {candidate.authenticity}%</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 p-6 overflow-hidden">
            <div className="text-center mb-6">
                <h2 className="text-sm font-bold tracking-wide">
                    {t("candidateEvaluation.titlePrefix")}
                    <span className="text-emerald-600"> {t("candidateEvaluation.titleHighlight")}</span>
                </h2>
                <p className="text-gray-500 mt-2 text-sm">
                    {t("candidateEvaluation.summary", { count: data.length })}
                </p>
            </div>

            {data.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[450px] text-center text-gray-400 gap-3">
                    <span className="text-5xl">📊</span>
                    <p className="text-sm font-medium">{t("candidateEvaluation.emptyTitle")}</p>
                    <p className="text-xs">{t("candidateEvaluation.emptyMessage")}</p>
                </div>
            ) : (
                <>
                    <div className="relative h-[450px]">
                        <div className="absolute top-[20px] bottom-[40px] left-[80px] right-[20px] grid grid-cols-2 grid-rows-2 pointer-events-none text-sm font-semibold">
                            <div className="flex items-start justify-start p-4 text-yellow-700">
                                {t("candidateEvaluation.quadrantDescriptions.roleAlignedUnauthentic")}
                            </div>

                            <div className="flex items-start justify-start p-4 text-emerald-700">
                                {t("candidateEvaluation.quadrantDescriptions.roleAlignedAuthentic")}
                            </div>

                            <div className="flex items-end justify-start p-4 text-red-600">
                                {t("candidateEvaluation.quadrantDescriptions.roleUnalignedUnauthentic")}
                            </div>

                            <div className="flex items-end justify-start p-4 text-yellow-700">
                                {t("candidateEvaluation.quadrantDescriptions.roleUnalignedAuthentic")}
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    dataKey="authenticity"
                                    domain={[0, 100]}
                                    label={{
                                        value: t("candidateEvaluation.authenticityQuotient"),
                                        position: "bottom",
                                    }}
                                />

                                <YAxis
                                    type="number"
                                    dataKey="alignment"
                                    domain={[0, 100]}
                                    label={{
                                        value: t("candidateEvaluation.roleAlignmentScore"),
                                        angle: -90,
                                        position: "insideLeft",
                                        dy: 100,
                                    }}
                                />

                                <ReferenceLine x={50} stroke="#10b981" />
                                <ReferenceLine y={50} stroke="#10b981" />
                                <Tooltip content={<CustomTooltip />} />

                                <Scatter
                                    data={data}
                                    fill="#2563eb"
                                    onClick={(point) => handleDotClick(point)}
                                />
                            </ScatterChart>
                        </ResponsiveContainer>

                        {/* Quadrant Click Areas */}
                        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pl-[5.1rem] pr-6 pt-6 pb-[3.3rem] pointer-events-none">
                            <div
                                className="pointer-events-auto hover:bg-yellow-200/20 cursor-pointer"
                                onClick={() => getCandidatesByQuadrant("skilledRisky")}
                            />
                            <div
                                className="pointer-events-auto hover:bg-green-200/20 cursor-pointer"
                                onClick={() => getCandidatesByQuadrant("highPerformers")}
                            />
                            <div
                                className="pointer-events-auto hover:bg-red-200/20 cursor-pointer"
                                onClick={() => getCandidatesByQuadrant("unsuitable")}
                            />
                            <div
                                className="pointer-events-auto hover:bg-yellow-200/20 cursor-pointer"
                                onClick={() => getCandidatesByQuadrant("genuineNeedsTraining")}
                            />
                        </div>
                    </div>
                </>
            )}

            <div
                className={`absolute top-0 right-0 h-full w-[320px] bg-white shadow-xl border-l z-30
                    transform transition-transform duration-300
                    ${isPanelOpen ? "translate-x-0" : "translate-x-full"}`}
            >
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="font-semibold text-gray-800 text-sm">
                        {selectedTitle}
                    </h3>
                    <button
                        onClick={() => setIsPanelOpen(false)}
                        className="text-gray-400 hover:text-gray-700"
                        aria-label={t("candidateEvaluation.close")}
                    >
                        ✕
                    </button>
                </div>

                <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-60px)]">
                    {filteredCandidates.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center mt-6">{t("candidateEvaluation.noCandidatesInQuadrant")}</p>
                    ) : (
                        filteredCandidates.map((c) => (
                            <div
                                key={c.name}
                                className="flex flex-col bg-gray-50 p-3 rounded-lg text-sm border gap-1"
                            >
                                <span className="font-medium text-gray-800">{c.name}</span>
                                <span className="text-gray-500 text-xs">
                                    {t("candidateEvaluation.ras")}: <span className="text-blue-600 font-semibold">{c.alignment}%</span>
                                    &nbsp;|&nbsp;
                                    {t("candidateEvaluation.aq")}: <span className="text-emerald-600 font-semibold">{c.authenticity}%</span>
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
